/** Snapping a click to the roads, so a new road joins an existing one exactly (prototype snapToPaths). */
import type { NodeId, PlanDoc, RoadId } from '../../shared/types';
import { lerpAt, nearestOnPoly } from '../geom/basic';
import type { Derived } from '../geom/derive';

/** Where a click snapped: on a road's centre line, or exactly on one of its dots (`nodeId`). */
export interface RoadSnapHit { roadId: RoadId; nodeId?: NodeId; x: number; y: number }

/**
 * Snaps (x, y) to the nearest road whose surface, or centre line within `tolMetres`, is under the click.
 * An existing dot within reach (the larger of tolMetres and the half width) wins over a spot on a centre line,
 * so junctions reuse dots. Null when no road is near.
 */
export function snapToRoads(doc: PlanDoc, derived: Derived, x: number, y: number, tolMetres: number): RoadSnapHit | null {
  let line: RoadSnapHit | null = null, lineD = Infinity;
  let dot: RoadSnapHit | null = null, dotD = Infinity;
  for (const [rid, road] of Object.entries(doc.roads)) {
    const poly = derived.roadPolys[rid];
    if (!poly || poly.pts.length < 2) continue;
    const q = nearestOnPoly(poly.pts, x, y), hw = lerpAt(poly.widths, q.i, q.t) / 2, reach = Math.max(tolMetres, hw);
    if (q.d > reach) continue;
    if (q.d < lineD) { lineD = q.d; line = { roadId: rid, x: q.px, y: q.py }; }
    for (const nid of road.nodes) {
      const n = doc.nodes[nid];
      if (!n) continue;
      const d = Math.hypot(n.x - x, n.y - y);
      if (d < reach && d < dotD) { dotD = d; dot = { roadId: rid, nodeId: nid, x: n.x, y: n.y }; }
    }
  }
  return dot ?? line;
}
